import { FC, useEffect } from 'react';
import { Col, Collapse, Row } from 'antd';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { getDefaultFormatDate } from '../../../common/utils/getFormatDate';
import DocumentsViewer from '../components/DocumentsViewer';
import { OnboardingFileKeys } from '../../../common/api/services/HistoryApi/common/OnboardingFileKeys';
import { ROUTER_PATHS } from '../../../navigation/routerPaths';
import { historyApi } from '../../../common/api/services/HistoryApi/historyApi';
import { setActiveFields, setActiveKeys } from '../../../redux/reducers/onboarding-history/sliceReducer';
import HistoryComponent from '../../onboardingPageV2/history/HistoryComponent';
import { FieldKeys } from '../../../common/api/services/HistoryApi/types/fieldKeys';
import { initialHistoryFields } from '../../onboardingPageV2/constants/initialHistoryFields';
import { CaretRightOutlined } from '@ant-design/icons';
import moment from 'moment';
import { commonDateDotsFormat } from '../../../common/constants/dateConstants';
const PersonalInfoTab: FC = () => {
    const dispatch = useAppDispatch();
    const {
        firstName,
        lastName,
        phone,
        email,
        dateOfBirth,
        gender,
        language,
        hasSwissAddress,
        hasAbroadAddress,
        hasOtherJob,
        documents,
        workForm,
        nationality,
    } = useAppSelector((state) => state.onboarding.personalInfo);
    const { activeKeys } = useAppSelector((state) => state.onBoardingHistory);

    useEffect(() => {
        const matchedKey = OnboardingFileKeys.find((item) =>
            Object.keys(item).some((key) => key === ROUTER_PATHS.personalInformation)
        );
        if (matchedKey) {
            historyApi
                .getOnboardingActiveFields({
                    userId: 'consultant',
                    keys: matchedKey[ROUTER_PATHS.personalInformation],
                })
                .then((resp) => dispatch(setActiveFields(resp.data)));
        }
    }, []);

    const collapseItems: any = [
        {
            key: '1',
            label: 'Personal details',
            children: (
                <div style={{ marginTop: '10px' }}>
                    <h4>General</h4>
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <div className="personal-info">
                                <span className="item-label">First name</span>
                                <span className="item-value">{firstName}</span>
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="personal-info">
                                <span className="item-label">Last name</span>
                                <span className="item-value">{lastName}</span>
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="personal-info">
                                <span className="item-label">Phone number</span>
                                <span className="item-value">{phone}</span>
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="personal-info">
                                <span className="item-label">Email</span>
                                <span className="item-value">{email}</span>
                            </div>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <div className="personal-info">
                                <span className="item-label">Date of birth</span>
                                <span className="item-value">{moment(dateOfBirth).format(commonDateDotsFormat)}</span>
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="personal-info">
                                <span className="item-label">Gender</span>
                                <span className="item-value">{gender}</span>
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="personal-info">
                                <span className="item-label">Language</span>
                                <span className="item-value">{language}</span>
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="personal-info">
                                <span className="item-label">Nationality</span>
                                <span className="item-value">{nationality}</span>
                            </div>
                        </Col>
                    </Row>
                    <HistoryComponent
                        fieldKey={FieldKeys.maritalStatus}
                        tabName="Marital status"
                        onChangeArray={[]}
                        shownElements={initialHistoryFields[FieldKeys.maritalStatus]}
                        isView
                    />
                    <div className="document-title" style={{ marginTop: '1rem' }}>
                        <h4>Documents (ID/Passport)</h4>
                        <DocumentsViewer documents={documents} />
                    </div>
                    <Row justify="start" style={{ padding: '0 5px' }}>
                        <Col span={12}>
                            <div className="personal-info">
                                <span className="item-label">How do you work?</span>
                                <span className="item-value">{workForm}</span>
                            </div>
                        </Col>
                    </Row>
                </div>
            ),
        },
        {
            key: '2',
            label: 'Address',
            children: (
                <>
                    <div className="address-title">
                        <h4>Address</h4>
                    </div>

                    <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ height: 54 }}>
                        <Col span={12}>
                            <div className="personal-info">
                                <span className="item-label"> Do you have a Swiss address?</span>
                                <span className="item-value">{hasSwissAddress ? 'Yes' : 'No'}</span>
                            </div>
                        </Col>
                    </Row>
                    {hasSwissAddress && (
                        <HistoryComponent
                            fieldKey={FieldKeys.swissAddress}
                            tabName="Swiss address"
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.swissAddress]}
                            isView
                        />
                    )}
                    <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ height: 54 }}>
                        <Col span={12}>
                            <div className="personal-info">
                                <span className="item-label">Do you have an address abroad?</span>
                                <span className="item-value">{hasAbroadAddress ? 'Yes' : 'No'}</span>
                            </div>
                        </Col>
                    </Row>
                    {hasAbroadAddress && (
                        <HistoryComponent
                            fieldKey={FieldKeys.abroadAddress}
                            tabName="Abroad address"
                            onChangeArray={[]}
                            shownElements={initialHistoryFields[FieldKeys.abroadAddress]}
                            isView
                        />
                    )}
                </>
            ),
        },
    ];

    const toggleAll = () => {
        if (activeKeys.length === collapseItems.length) {
            dispatch(setActiveKeys([]));
        } else {
            dispatch(setActiveKeys(collapseItems.map((item: any) => item.key)));
        }
    };

    return (
        <div className="onboarding-wrapper-v2">
            <Row justify="end">
                <span className="toggle-button-onboarding" onClick={toggleAll}>
                    {activeKeys.length === collapseItems.length ? 'Collapse All' : 'Expand All'}{' '}
                    <CaretRightOutlined rotate={activeKeys.length === collapseItems.length ? 270 : 90} />
                </span>
            </Row>
            <Collapse
                activeKey={activeKeys}
                onChange={(keys) => dispatch(setActiveKeys(keys as string[]))}
                style={{ width: '100%' }}
                className="onboarding-collapse"
                expandIconPosition="right"
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 270 : 90} />}
            >
                {collapseItems.map((item: any) => {
                    return (
                        <Collapse.Panel key={item.key} header={<span>{item.label}</span>}>
                            {item.children}
                        </Collapse.Panel>
                    );
                })}
            </Collapse>
        </div>
    );
};

export default PersonalInfoTab;
